import { ArrowLeft, ArrowRight, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Autoplay, EffectFade } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { mapCmsNewsToBannerSlide } from '../api/cmsNews';
import { USE_STATIC_SITE_CONTENT } from '../config/staticSiteMode';
import { DUMMY_BANNER_SLIDES } from '../data/bannerSlides';
import { STATIC_HOME_BANNER_NEWS } from '../data/staticSite/content';

import 'swiper/css';
import 'swiper/css/effect-fade';

const AUTOPLAY_DELAY = 5000;

/**
 * @param {{ href: string, className?: string, children: React.ReactNode, 'aria-label'?: string }} props
 */
function BannerSlideLink({ href, className, children, 'aria-label': ariaLabel }) {
    const isExternal =
        href.startsWith('http://') || href.startsWith('https://');
    if (isExternal) {
        return (
            <a
                href={href}
                className={className}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={ariaLabel}
            >
                {children}
            </a>
        );
    }
    return (
        <Link to={href} className={className} aria-label={ariaLabel}>
            {children}
        </Link>
    );
}

/**
 * @param {{
 *   slide: { title: string, description: string, link: string },
 *   slideIndex1: number,
 *   showNav: boolean,
 *   swiperRef: React.MutableRefObject<import('swiper').Swiper | null>,
 *   autoplayFill: number,
 * }} props
 */
function BannerSlideGlassPanel({
    slide,
    slideIndex1,
    showNav,
    swiperRef,
    autoplayFill,
}) {
    const { t } = useTranslation();
    return (
        <div className="banner-slide-glass-panel z-1">
            <div className="container-fluid px-0 w-100">
                <div className="row align-items-center gy-3 gx-lg-5 banner-slide-glass-row mx-0">
                    <div className="col-lg-6 banner-slide-glass-col-head">
                        <h2 className="banner-slide-glass-title mb-0">
                            {slide.title}
                        </h2>
                        <div className="banner-glass-cta-wrap banner-glass-cta-wrap--desktop d-none d-lg-flex flex-wrap align-items-center gap-3 mt-3 mt-lg-4">
                            <BannerSlideLink
                                href={slide.link}
                                className="banner-glass-cta text-decoration-none d-flex align-items-center"
                            >
                                {t('home.banner.readMore')}
                                <ChevronRight
                                    size={15}
                                    className="ms-2"
                                />
                            </BannerSlideLink>
                        </div>
                    </div>
                    <div className="col-lg-6 banner-slide-glass-col-meta">
                        <div className="d-flex align-items-center justify-content-between gap-3">
                            <span className="banner-slide-index">
                                {String(slideIndex1).padStart(2, '0')}
                            </span>
                            {showNav && (
                                <div className="d-flex align-items-center gap-1">
                                    <button
                                        type="button"
                                        className="banner-glass-nav-btn text-white"
                                        aria-label={t('home.banner.prevSlide')}
                                        onClick={() =>
                                            swiperRef.current?.slidePrev()
                                        }
                                    >
                                        <ArrowLeft
                                            size={22}
                                            strokeWidth={1.35}
                                            aria-hidden
                                        />
                                    </button>
                                    <button
                                        type="button"
                                        className="banner-glass-nav-btn text-white"
                                        aria-label={t('home.banner.nextSlide')}
                                        onClick={() =>
                                            swiperRef.current?.slideNext()
                                        }
                                    >
                                        <ArrowRight
                                            size={22}
                                            strokeWidth={1.35}
                                            aria-hidden
                                        />
                                    </button>
                                </div>
                            )}
                        </div>
                        {showNav && (
                            <div
                                className="banner-progress-track mt-3"
                                role="progressbar"
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-valuenow={Math.round(
                                    Math.min(1, Math.max(0, autoplayFill)) *
                                    100
                                )}
                                aria-label={t('home.banner.autoplayProgress')}
                            >
                                <div
                                    className="banner-progress-fill"
                                    style={{
                                        width: `${Math.min(1, Math.max(0, autoplayFill)) * 100}%`,
                                    }}
                                />
                            </div>
                        )}
                        <p className="banner-slide-glass-desc mt-3 mb-0">
                            {slide.description}
                        </p>
                    </div>
                    <div className="col-12 banner-glass-cta-wrap banner-glass-cta-wrap--mobile d-lg-none">
                        <BannerSlideLink
                            href={slide.link}
                            className="banner-glass-cta text-decoration-none d-inline-flex align-items-center"
                        >
                            {t('home.banner.readMore')}
                            <ChevronRight size={15} className="ms-2" />
                        </BannerSlideLink>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * @typedef {{
 *   id: string,
 *   imageUrl: string,
 *   title: string,
 *   description: string,
 *   link: string,
 * }} BannerSlide
 */

/**
 * @param {{
 *   slides?: BannerSlide[] | null,
 *   leadSlide?: BannerSlide | null,
 *   latestNewsCount?: number,
 *   maxSlides?: number,
 * }} props
 * `slides` verilirse API çağrısı yapılmaz. `leadSlide` + `latestNewsCount` ana sayfa düzeni.
 */
export function BannerCarousel({
    slides: slidesProp = null,
    leadSlide = null,
    latestNewsCount = 0,
    maxSlides,
}) {
    const { t } = useTranslation();
    const swiperRef = useRef(null);
    const [slides, setSlides] = useState(
        () => slidesProp ?? null,
    );
    const [autoplayFill, setAutoplayFill] = useState(0);
    useEffect(() => {
        if (slidesProp != null) {
            setSlides(
                slidesProp.length > 0 ? slidesProp : DUMMY_BANNER_SLIDES,
            );
            return;
        }

        if (USE_STATIC_SITE_CONTENT) {
            /** @type {BannerSlide[]} */
            let combined = [];
            if (leadSlide?.imageUrl) {
                combined.push(leadSlide);
            }
            const newsSlides = STATIC_HOME_BANNER_NEWS.map(
                mapCmsNewsToBannerSlide,
            ).filter((s) => s.imageUrl);
            combined = combined.concat(newsSlides);
            if (latestNewsCount > 0) {
                combined = combined.slice(
                    0,
                    1 + Math.min(latestNewsCount, newsSlides.length),
                );
            }
            if (maxSlides != null && maxSlides > 0) {
                combined = combined.slice(0, maxSlides);
            }
            setSlides(combined.length > 0 ? combined : DUMMY_BANNER_SLIDES);
            return undefined;
        }

        let cancelled = false;

        /*
        (async () => {
            try {
                let combined = [];
                if (leadSlide?.imageUrl) {
                    combined.push(leadSlide);
                }
                if (latestNewsCount > 0) {
                    const items = await fetchTopCmsNewsByOrder(
                        latestNewsCount,
                    );
                    const newsSlides = items
                        .map(mapCmsNewsToBannerSlide)
                        .filter((s) => s.imageUrl);
                    combined = combined.concat(newsSlides);
                } else if (!leadSlide) {
                    const items = await fetchCmsNewsForBanner();
                    combined = items
                        .map(mapCmsNewsToBannerSlide)
                        .filter((s) => s.imageUrl);
                }
                if (maxSlides != null && maxSlides > 0) {
                    combined = combined.slice(0, maxSlides);
                }
                if (cancelled) return;
                setSlides(
                    combined.length > 0 ? combined : DUMMY_BANNER_SLIDES,
                );
            } catch {
                if (cancelled) return;
                if (leadSlide?.imageUrl) {
                    setSlides([leadSlide]);
                    return;
                }
                setSlides(DUMMY_BANNER_SLIDES);
            }
        })();
        */

        return () => {
            cancelled = true;
        };
    }, [slidesProp, leadSlide, latestNewsCount, maxSlides]);

    if (slides == null) {
        return (
            <section
                className="banner-carousel position-relative"
                aria-label={t('home.banner.sectionLabel')}
                aria-busy="true"
            >
                <div className="banner-carousel-inner">
                    <div className="banner-slide banner-slide--placeholder" />
                </div>
            </section>
        );
    }

    const showNav = slides.length > 1;

    if (slides.length === 0) {
        return null;
    }

    return (
        <section
            className="banner-carousel position-relative"
            aria-label={t('home.banner.sectionLabel')}
        >
            <div className="banner-carousel-inner">
            <Swiper
                className="banner-carousel-swiper"
                modules={[EffectFade, Autoplay]}
                effect="fade"
                fadeEffect={{ crossFade: true }}
                speed={600}
                slidesPerView={1}
                spaceBetween={0}
                loop={showNav}
                pagination={false}
                autoplay={
                    showNav
                        ? {
                            delay: AUTOPLAY_DELAY,
                            disableOnInteraction: true,
                        }
                        : false
                }
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                }}
                onSlideChangeTransitionStart={() => setAutoplayFill(0)}
                onAutoplayTimeLeft={(_swiper, _timeLeft, timeRemainingRatio) => {
                    setAutoplayFill(1 - timeRemainingRatio);
                }}
            >
                {slides.map((slide, index) => (
                    <SwiperSlide key={slide.id}>
                        <div className="banner-slide">
                            <div className="banner-slide-media">
                                <img
                                    src={slide.imageUrl}
                                    alt={slide.title}
                                    className="banner-slide-img"
                                    loading={index === 0 ? 'eager' : 'lazy'}
                                    decoding="async"
                                />
                            </div>
                            <BannerSlideGlassPanel
                                slide={slide}
                                slideIndex1={index + 1}
                                showNav={showNav}
                                swiperRef={swiperRef}
                                autoplayFill={autoplayFill}
                            />
                        </div>
                    </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </section>
    );
}
